import { RemoveStructuredBriefDesignersVariables } from '__generated__/RemoveStructuredBriefDesigners';
import { TabularMeta_User } from '__generated__/TabularMeta_User';
import { handlePromise } from 'common/utils/Bugsnag';
import { trackRemovedDesignerEvent } from 'projects/events';
import RemoveStructuredBriefDesigners from 'projects/mutations/RemoveStructuredBriefDesigners';
import { springConfig } from 'projects/util/spring';
import * as React from 'react';
import { Transition } from 'react-spring/renderprops';
import Avatar from './Avatar';

type Brief = {
    id: string;
    designers: TabularMeta_User[];
};

interface Props {
    brief: Brief;
}

interface State {
    expanded: boolean;
}

class DesignerStack extends React.Component<Props, State> {
    state = {
        expanded: false,
    };

    componentDidUpdate(prevProps: Props) {
        if (prevProps.brief.designers.length > 0 && this.props.brief.designers.length === 0) {
            this.setState({ expanded: false });
        }
    }

    toggleExpanded = () => {
        this.setState({ expanded: !this.state.expanded });
    };

    removeDesigner = (
        designer: TabularMeta_User,
        removeDesigners: (variables: RemoveStructuredBriefDesignersVariables) => Promise<any>
    ) => {
        const { brief } = this.props;

        trackRemovedDesignerEvent({
            id: brief.id,
            designer_id: designer.id,
        });
        handlePromise(
            removeDesigners({
                id: brief.id,
                designerIds: [designer.id],
            }),
            'DesignerStack RemoveDesigners'
        );
    };

    renderDesignerStackItems = (
        removeDesigners: (variables: RemoveStructuredBriefDesignersVariables) => Promise<any>
    ) => {
        const { brief } = this.props;
        const { expanded } = this.state;

        return (
            <div className="designer-stack__items">
                <Transition
                    items={
                        this.state.expanded
                            ? brief.designers
                            : brief.designers.length > 0
                            ? [brief.designers[0]]
                            : []
                    }
                    keys={designer => designer.id}
                    from={{ height: 0, opacity: 0 }}
                    enter={{ height: 'auto', opacity: 1 }}
                    leave={{ height: 0, opacity: 0 }}
                    config={springConfig(250)}
                    trail={20}
                >
                    {(designer, state, index) => props => {
                        const firstItem = index === 0;
                        const leave = 'leave';

                        return (
                            <div
                                key={designer.id}
                                style={
                                    !firstItem || state === leave
                                        ? { ...props }
                                        : { cursor: 'pointer' }
                                }
                            >
                                <div className="designer-stack__items__avatar">
                                    <Avatar
                                        userId={designer.id}
                                        onRemove={
                                            this.state.expanded
                                                ? () =>
                                                      this.removeDesigner(designer, removeDesigners)
                                                : undefined
                                        }
                                        onClick={firstItem ? this.toggleExpanded : undefined}
                                        avatarCount={
                                            firstItem && !expanded
                                                ? brief.designers.length
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        );
                    }}
                </Transition>
            </div>
        );
    };

    render() {
        const { expanded } = this.state;

        return (
            <RemoveStructuredBriefDesigners>
                {removeDesigners => (
                    <Transition
                        items={expanded}
                        from={{ opacity: 0 }}
                        enter={{ opacity: 1 }}
                        leave={{ opacity: 0 }}
                        config={springConfig(300)}
                    >
                        {expanded => props => (
                            <>
                                {expanded && (
                                    <div
                                        className="designer-stack__overlay"
                                        style={{ ...props }}
                                        onClick={this.toggleExpanded}
                                    />
                                )}
                                <div className="designer-stack">
                                    {this.renderDesignerStackItems(removeDesigners)}
                                </div>
                            </>
                        )}
                    </Transition>
                )}
            </RemoveStructuredBriefDesigners>
        );
    }
}

export default DesignerStack;
